/** @jsxImportSource @emotion/react */

// libraries
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { css } from "@emotion/react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";

// model, data
import {
  HALF_AND_QUARTERS,
  HalfYear,
  MONTHS,
  Quarter,
  YearMonth,
} from "data/period.data";
import {
  REVIEW_COL_DATA,
  ReviewBankStatementColumnData,
  ReviewCardBuyColumnData,
  ReviewTaxInvoiceColumnData,
} from "./column.data";
import { PALETTE } from "styles/base/variables";
import {
  Business,
  CardBuy,
  MenuType,
  MultiAccountCodeAmount,
  Store,
  StoreType,
  SUB_TYPE_PARAM_KEYS,
  SubType,
  UpdateTxData,
} from "models/business.model";
import { SUBJECT_DATA, SUBJECT_LIST } from "data/subject.data";
import { SAMPLE_CARDBUY_DATA } from "data/sample.data";
import {
  BankStatement,
  BankStatementWithAdditionalData,
} from "models/bank.model";
import { TaxInvoice, TaxItem } from "models/tax.model";
import { bankAccountNoSplitPipe } from "pipes/bank.pipe";

// components
import Table from "components/common/Table";
import BaseSelect from "components/common/Select/Base";
import BaseInput from "components/common/Input/Base";
import SubtitleCard from "components/common/Card/Subtitle/SubtitleCard";
import BaseButton from "components/common/Button/Base";
import ReportButton from "components/Report/Button";
import AlertDialog, { AlertData } from "components/common/Dialog/Alert";
import MultiAccountDialog from "./Dialog/MultiAccount";
import SelectStoreDialog, {
  SelectStoreDialogSettings,
} from "./Dialog/SelectStore";

// utils, api
import {
  BaseParamData,
  downloadNewzenExcel,
  getStore,
  updateConfirmation,
  updateTransaction,
} from "pages/remotes";
import {
  convertDateToPeriod,
  convertQuarterAndHalfToDate,
  convertYearMonthToDate,
  reverseArray,
  sortList,
} from "utils/converter";
import { renderDownload } from "utils/renderer";

// icons
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { SubjectCodeData } from "models/subject.model";

const checkLimitNumber = 100;

function Review(data: { storeType: StoreType; subType?: SubType }) {
  const navigate = useNavigate();
  const location = useLocation();
  const storeType: StoreType = data.storeType;
  const subType: SubType | undefined = data.subType;
  const menuType = (
    subType ? `${storeType}-${subType.toLowerCase()}` : storeType
  ) as MenuType;

  const locationBizId = location.pathname.split("/")?.[3];
  const [searchParams, setSearchParams] = useSearchParams();
  const [spStartDate, spEndDate, spSort, spSample] = [
    searchParams.get("startDate"),
    searchParams.get("endDate"),
    searchParams.get("sort"),
    searchParams.get("sample"),
  ];
  const inputKeywordRef = useRef<HTMLInputElement>(null);
  const inputSubjectRef = useRef<HTMLInputElement>(null);
  const optionSubjectRef = useRef<HTMLUListElement>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [business, setBusiness] = useState<Business | null>(null); // BusinessWithCardBuyList | BusinessWithBankStatementList | BusinessWithTaxInvoiceList 이지만 편의상 Business로 설정

  const [columnList, setColumnList] = useState<
    (
      | ReviewCardBuyColumnData
      | ReviewBankStatementColumnData
      | ReviewTaxInvoiceColumnData
    )[]
  >([]);

  const [dataList, setDataList] = useState<
    (CardBuy | BankStatementWithAdditionalData)[]
  >([]);
  const [sortedDataList, setSortedDataList] = useState<
    (CardBuy | BankStatementWithAdditionalData)[]
  >([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [filteredDataList, setFilteredDataList] = useState<
    (CardBuy | BankStatementWithAdditionalData)[]
  >([]);
  const [checkedDataSet, setCheckedDataSet] = useState<Set<string>>(new Set());
  const [modifiedDataMap, setModifiedDataMap] = useState<
    Map<string, UpdateTxData>
  >(new Map());

  const [isSubjectListOpen, setIsSubjectListOpen] = useState(false);
  const [modifiedSubjectList, setModifiedSubjectList] = useState<
    { value: string; label: string }[]
  >([]);
  const [filteredSubjectList, setFilteredSubjectList] =
    useState<{ value: string; label: string }[]>(modifiedSubjectList);
  const [subjectCodeDataList, setSubjectCodeDataList] = useState<
    SubjectCodeData[]
  >([]);

  const [selectedDdcAll, setSelectedDdcAll] = useState<string>("");
  const [isCheckOverLimit, setIsCheckOverLimit] = useState(false);
  const [isUnsavedAlertOpen, setIsUnsavedAlertOpen] = useState<boolean>(false);
  const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
  const [alertDialogData, setAlertDialogData] = useState<AlertData>({
    message: "",
  });
  const [isShowUnprocessed, setIsShowUnprocessed] = useState<boolean>(false);
  const [isMultiAccountDialogOpen, setIsMultiAccountDialogOpen] =
    useState<boolean>(false);
  const [selectedMAData, setSelectedMAData] =
    useState<BankStatementWithAdditionalData | null>(null);

  const [storeList, setStoreList] = useState<Store[]>([]);
  const [isSaveOptionOpen, setIsSaveOptionOpen] = useState<boolean>(false);
  const [isRegisterAccountCode, setIsRegisterAccountCode] =
    useState<boolean>(false);
  const [isSelectDialogOpen, setIsSelectStoreDialogOpen] =
    useState<boolean>(false);
  const [selectedStoreDialogSettings, setSelectedStoreDialogSettings] =
    useState<SelectStoreDialogSettings>({
      storeId: null,
      dataIds: [],
      isCancel: false,
    });

  useEffect(() => {
    const tmpColumns = REVIEW_COL_DATA[menuType] || [];
    if (tmpColumns.length === 0) {
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "error";
      tmpAlertData.message = "테이블 칼럼을 지원하지 않는 스토어 타입입니다.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      return;
    } else {
      setColumnList(
        tmpColumns.map((col) => {
          // isVatDeduction 은행 입출금내역에는 미존재
          switch (col.id) {
            case "isVatDeduction":
              return {
                ...col,
                doubleClick: {
                  submit: (value, dataId) => {
                    const changedDdc = !value; // 클릭 당시 공제 상태의 반대로 전달
                    modifyDdc([{ isVatDeduction: changedDdc, id: dataId }]);
                  },
                },
              };
            case "storeName":
              if (storeType === "bank") {
                return {
                  ...col,
                  openDialog: openSelectStoreDialog,
                  cancel: (data: BankStatementWithAdditionalData) =>
                    openSelectStoreDialog(data, { isClose: true }),
                };
              } else return col;
            case "accountCode":
              const extendedCol:
                | ReviewCardBuyColumnData
                | ReviewBankStatementColumnData
                | ReviewTaxInvoiceColumnData = {
                ...col,
                input: {
                  submit: (value, dataId) => modifySubjectCode(value, dataId),
                  options: modifiedSubjectList,
                },
                restore: ({ dataId, colId }) => {
                  const modifiedCurrData = modifiedDataMap.get(dataId);
                  if (modifiedCurrData && colId === "accountCode")
                    delete modifiedCurrData.accountCode;

                  setModifiedDataMap(new Map(modifiedDataMap));
                },
              };

              if (storeType === "bank")
                return {
                  ...extendedCol,
                  openDialog: openMultiAccountCodeDialog,
                };
              else return extendedCol;
            default:
              return col;
          }
        })
      );
    }
  }, [modifiedDataMap, dataList]);
  // 위 useEffect 통장도 똑같이 만들기

  useEffect(() => {
    setSortedDataList(sortList([...dataList], spSort));
  }, [spSort]);

  useEffect(() => {
    const keyword = inputKeywordRef.current?.value || "";
    if (keyword)
      inputKeywordHandler({
        currentTarget: { value: keyword },
      } as React.FormEvent<HTMLInputElement>); // 검색어가 있는 경우 검색어 필터링
  }, [sortedDataList]);

  useEffect(() => {
    loadStore();
  }, [spStartDate, spEndDate]);

  useEffect(() => {
    if (checkedDataSet.size === 0 || checkedDataSet.size > checkLimitNumber) {
      setIsCheckOverLimit(true);
      setSelectedDdcAll("");
      if (isSubjectListOpen) setIsSubjectListOpen(false);
    } else {
      setIsCheckOverLimit(false);
    }
  }, [checkedDataSet]);

  useEffect(() => {
    if (!isSubjectListOpen) resetInputOption();
  }, [isSubjectListOpen]);

  const loadStore = () => {
    if (!(spStartDate && spEndDate)) {
      const tmpAlertData = { ...alertDialogData };
      tmpAlertData.type = "error";
      tmpAlertData.message = "검색 기간이 없습니다. 검색 기간을 설정해주세요.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      return;
    }

    setIsLoading(true);
    let paramData: BaseParamData = {
      startDate: spStartDate!,
      endDate: spEndDate!,
    };
    const subTypeKey = SUB_TYPE_PARAM_KEYS[storeType] as SubType;
    if (subType && subTypeKey) {
      paramData = {
        ...paramData,
        [subTypeKey]: subType,
      };
    }

    getStore(storeType, locationBizId, paramData)
      .then((res) => {
        const resData = res.data;
        if (resData) {
          setBusiness(resData);
          let dataList: (CardBuy | BankStatement | TaxInvoice)[] = [];
          let stores: Store[] = [];
          let sbjData: { [key: string]: string } = {};
          let sbjList: SubjectCodeData[] = resData.accountCodes || [];
          let sbjModiList: { value: string; label: string }[] = [];

          if (storeType === "card") {
            if (spSample) dataList = SAMPLE_CARDBUY_DATA;
            else dataList = resData.cardBuyList;
          } else if (storeType === "bank") {
            dataList = resData.bankStatementList;
            stores = resData.stores || [];
          } else if (storeType === "tax") dataList = resData.invoiceList;

          if (sbjList.length > 0) {
            sbjData = sbjList.reduce((acc, curr) => {
              acc[curr.code] = curr.name;
              return acc;
            }, {} as { [key: string]: string });

            sbjModiList = sbjList.map((subject) => ({
              value: subject.code,
              label: `${subject.name} (${subject.code})`,
            }));
          } else {
            sbjData = SUBJECT_DATA;
            sbjModiList = SUBJECT_LIST.map((subject) => ({
              ...subject,
              label: subject.label + ` (${subject.value})`,
            }));
          }

          setSubjectCodeDataList(sbjList);
          setModifiedSubjectList(sbjModiList);
          setFilteredSubjectList(sbjModiList);
          setStoreList(stores);

          if (dataList && dataList.length > 0) {
            const dataListWithConvertedInfo = dataList.map((data) => {
              const subjectCode = data.accountCode || "";
              const subjectName = sbjData[subjectCode] || "";

              const additionalData: any = {};
              additionalData.accountName = subjectName;
              if (storeType === "bank") {
                const tmpData = data as BankStatement;
                const isInAmt =
                  tmpData.inAmt > 0 ||
                  tmpData.inAmt < 0 ||
                  tmpData.inAmt - tmpData.outAmt === 0;

                additionalData.dealType =
                  tmpData.inAmt !== null && isInAmt ? "입금" : "출금";

                additionalData.amount = isInAmt
                  ? tmpData.inAmt
                  : tmpData.outAmt;

                additionalData.accNoSplit = bankAccountNoSplitPipe(
                  tmpData.accNo
                );

                if (
                  tmpData.multiAccountCodes &&
                  tmpData.multiAccountCodes.length > 0
                ) {
                  let [accCodeStr, accNameStr] = ["", ""];
                  tmpData.multiAccountCodes.forEach(
                    (acc: MultiAccountCodeAmount) => {
                      if (!accCodeStr) accCodeStr = acc.accountCode;
                      if (accNameStr) accNameStr += ", ";
                      const accCodeName = sbjData[acc.accountCode];
                      if (accCodeName) accNameStr += accCodeName;
                      else accNameStr += acc.accountCode;
                    }
                  );

                  additionalData.accountCode =
                    accCodeStr + " +" + (tmpData.multiAccountCodes.length - 1);
                  additionalData.accountName = accNameStr;
                }

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNoList?.length) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              } else if (storeType === "card") {
                const tmpData = data as CardBuy;

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNo) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              } else if (storeType === "tax") {
                const tmpData = data as TaxInvoice;
                additionalData.tranType =
                  tmpData.supByr === "01"
                    ? "매출"
                    : tmpData.supByr === "02"
                    ? "매입"
                    : "";
                additionalData.taxType = tmpData.type
                  ? tmpData.isZeroTax
                    ? "영세"
                    : tmpData.type.includes("FREE")
                    ? "면세"
                    : "과세"
                  : "";

                const items: TaxItem[] = tmpData.items || [];
                let assortedItemNames = "";
                items.map((item) => {
                  if (item.itemNm) {
                    if (assortedItemNames) assortedItemNames += ", ";
                    assortedItemNames += item.itemNm;
                  }
                });
                additionalData.itemsName = assortedItemNames || "-";

                let isNewzenSent = false;
                if (tmpData.newzenSlipKeyNo) isNewzenSent = true;
                additionalData.newzenSent = isNewzenSent;
              }

              return {
                ...data,
                ...additionalData,
              };
            });

            setDataList(dataListWithConvertedInfo);
            setSortedDataList(sortList(dataListWithConvertedInfo, spSort));
          } else {
            setDataList([]);
            setSortedDataList([]);
          }

          setModifiedDataMap(new Map());
          setCheckedDataSet(new Set());
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        const tmpAlertData = { ...alertDialogData };
        tmpAlertData.type = "error";
        tmpAlertData.message = "업체 정보를 불러오지 못했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      });
  };

  const onSelectQuarter = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertQuarterAndHalfToDate(
      value as Quarter | HalfYear
    );
    if (startDate && endDate) {
      searchParams.set("startDate", startDate);
      searchParams.set("endDate", endDate);
    }
    setSearchParams(searchParams);
    setCheckedDataSet(new Set());
  };
  const onSelectMonth = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const { startDate, endDate } = convertYearMonthToDate(value as YearMonth);
    if (startDate && endDate) {
      searchParams.set("startDate", startDate);
      searchParams.set("endDate", endDate);
    }
    setSearchParams(searchParams);
    setCheckedDataSet(new Set());
  };

  const inputKeywordHandler = (e: React.FormEvent<HTMLInputElement>) => {
    const tmpColumns = REVIEW_COL_DATA[menuType] || [];

    const value = e.currentTarget.value || "";
    const loweredVal = value.toLowerCase();
    if (value) {
      setIsFiltered(true);
      let tmpSortedDataList = sortedDataList.filter((cardBuy) => {
        for (const column of tmpColumns) {
          const colId = column.id as keyof (
            | CardBuy
            | BankStatementWithAdditionalData
          );
          const loweredContent = cardBuy[colId]?.toString().toLowerCase() || "";
          if (loweredContent?.includes(loweredVal)) return true;
        }
      });
      setFilteredDataList(tmpSortedDataList);
    } else {
      setIsFiltered(false);
    }
  };

  const onSelectDdcAll = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const updates: UpdateTxData[] = [];
    checkedDataSet.forEach((id) =>
      updates.push({ isVatDeduction: value === "y", id })
    );
    modifyDdc(updates);
    setSelectedDdcAll("");
  };

  const onSelectSbjAll = (e: React.MouseEvent<HTMLOptionElement>) => {
    const value = e.currentTarget.value || "";
    const updates: UpdateTxData[] = [];

    checkedDataSet.forEach((id) => updates.push({ id, accountCode: value }));
    updates.forEach(
      (update) =>
        update.accountCode && modifySubjectCode(update.accountCode, update.id)
    );
  };

  const onSearchSbjAll = (e: React.FormEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value || "";
    if (value) {
      setFilteredSubjectList(
        modifiedSubjectList.filter((subject) => subject.label.includes(value))
      );
    } else {
      setFilteredSubjectList(modifiedSubjectList);
    }
  };

  const keyDownHanlder = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const event = e as React.KeyboardEvent<HTMLInputElement>;
    const eventKey = event.key;
    const eventTarget = event.target as HTMLInputElement;
    const tgtValue = eventTarget?.value;

    if (eventKey === "Enter") {
      if (tgtValue) {
        const firstMatch = modifiedSubjectList.find((subject) =>
          subject.label.includes(tgtValue)
        );

        if (firstMatch) {
          onSelectSbjAll({
            currentTarget: { value: firstMatch.value },
          } as unknown as React.MouseEvent<HTMLOptionElement>);
        }
      }
    } else if (eventKey === "Escape") {
      setIsSubjectListOpen(false);
    }
  };

  const resetInputOption = () => {
    if (inputSubjectRef.current) inputSubjectRef.current.value = "";
    if (optionSubjectRef.current) optionSubjectRef.current.scrollTop = 0;
    setFilteredSubjectList(modifiedSubjectList);
  };

  const modifyDdc = (updates: UpdateTxData[]) => {
    let tmpMCB;
    updates.forEach((update) => {
      tmpMCB = renderMCBDdc(update, update.id);
    });
    if (tmpMCB) setModifiedDataMap(tmpMCB);
  };

  const renderMCBDdc = (
    update: UpdateTxData,
    dataId: string
  ): Map<string, UpdateTxData> => {
    const tmpMatchedData = dataList.find(
      (data) => data.id === dataId
    ) as CardBuy; // 은행입출금내역은 공제 항목이 없음
    const originalDdc = tmpMatchedData?.isVatDeduction;
    const tmpMCB = modifiedDataMap;
    const isNotChanged = originalDdc === update.isVatDeduction;

    if (tmpMCB.has(dataId)) {
      const newData = tmpMCB.get(dataId);
      if (isNotChanged) {
        if (newData?.accountCode)
          delete newData.isVatDeduction; // 계정과목 변동이 있을 경우 공제 여부 변경만 삭제
        else tmpMCB.delete(dataId); // 계정과목 변동이 없을 경우 삭제
      } else {
        const updatedData: UpdateTxData = {
          id: dataId,
          isVatDeduction: update.isVatDeduction,
        };
        if (newData?.accountCode)
          updatedData["accountCode"] = newData.accountCode;
        tmpMCB.set(dataId, updatedData);
      }
    } else {
      if (isNotChanged) {
        console.log("No change");
      } else {
        tmpMCB.set(dataId, {
          id: dataId,
          isVatDeduction: update.isVatDeduction,
        });
      }
    }

    return new Map(tmpMCB);
  };

  const modifySubjectCode = (value: string, dataId: string) => {
    if (modifiedDataMap.has(dataId)) {
      const tmpModi = modifiedDataMap.get(dataId);
      if (tmpModi) {
        const matchedData = dataList.find((data) => data.id === dataId);
        if (matchedData?.accountCode === value) {
          // 계정과목이 변경되지 않았을 경우
          if (tmpModi.isVatDeduction !== undefined)
            delete tmpModi.accountCode; // 공제 여부 변경이 있을 경우 계정과목 변경만 삭제
          else modifiedDataMap.delete(dataId); // 공제 여부 변경이 없을 경우 삭제
        } else {
          tmpModi.accountCode = value;
        }
      }
    } else {
      modifiedDataMap.set(dataId, {
        id: dataId,
        accountCode: value,
      });
    }

    setModifiedDataMap(new Map(modifiedDataMap));
  };

  const openSelectStoreDialog = (
    data: BankStatementWithAdditionalData,
    option?: { isClose?: boolean }
  ) => {
    setSelectedStoreDialogSettings({
      storeId: data.storeId,
      dataIds: data.id ? [data.id] : [],
      isCancel: option?.isClose || false,
    });
    setIsSelectStoreDialogOpen(true);
  };

  const closeSelectStoreDialog = () => {
    setIsSelectStoreDialogOpen(false);
    setTimeout(() => {
      resetInputOption();
      setSelectedStoreDialogSettings({
        storeId: null,
        dataIds: [],
        isCancel: false,
      });
    }, 300);
  };

  const onSelectStoreAll = () => {
    const checkedIds = Array.from(checkedDataSet).map((dataId) => dataId);
    setSelectedStoreDialogSettings({
      storeId: null,
      dataIds: checkedIds,
      isCancel: false,
    });
    setIsSelectStoreDialogOpen(true);
  };

  const openMultiAccountCodeDialog = (
    data: BankStatementWithAdditionalData
  ) => {
    setSelectedMAData(data);
    setIsMultiAccountDialogOpen(true);
  };

  const closeMultiAccountCodeDialog = () => {
    setSelectedMAData(null);
    setIsMultiAccountDialogOpen(false);
  };

  const downloadExcel = (type: StoreType) => {
    const paramData: {
      startDate: string;
      endDate: string;
      invoiceType?: string;
    } = {
      startDate: spStartDate || "2023-01-01",
      endDate: spEndDate || "2023-12-31",
    };

    if (type === "tax") paramData["invoiceType"] = subType || "ALL";

    const tmpAlertData = { ...alertDialogData };
    setIsDownloadLoading(true);
    downloadNewzenExcel(type, locationBizId, paramData).then(
      (res) => {
        setIsDownloadLoading(false);
        renderDownload(res);
        tmpAlertData.type = "success";
        tmpAlertData.message = "엑셀 파일이 다운로드되었습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      },
      (err) => {
        console.error(err);
        setIsDownloadLoading(false);
        tmpAlertData.type = "error";
        tmpAlertData.message = "엑셀 파일 다운로드에 실패했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
      }
    );
  };

  const saveModified = (addAction?: string) => {
    const updateDataList: UpdateTxData[] = [];
    modifiedDataMap.forEach((data) => updateDataList.push(data));

    const paramData: { registerAccountCode?: boolean } = {};
    if (isRegisterAccountCode) paramData.registerAccountCode = true;

    const isConfirm = addAction === "confirm";
    if (isConfirm) setIsConfirmLoading(true);
    updateTransaction(storeType, locationBizId, updateDataList, paramData)
      .then((res) => {
        loadStore();
        if (isConfirm) handleConfirm();
      })
      .catch((err) => {
        if (isConfirm) setIsConfirmLoading(false);

        const errMsg = err.response?.data?.message || "오류가 발생했습니다.";
        if (errMsg) {
          const tmpAlertData = { ...alertDialogData };
          tmpAlertData.type = "error";
          tmpAlertData.message = errMsg;
          setAlertDialogData(tmpAlertData);
          setIsAlertOpen(true);
        }
      });
  };

  const handleConfirm = () => {
    const tmpAlertData = { ...alertDialogData };
    if (!business) {
      tmpAlertData.type = "error";
      tmpAlertData.message = "업체 정보를 불러오지 못했습니다.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      if (isConfirmLoading) setIsConfirmLoading(false);
      return;
    }

    if (!(spStartDate && spEndDate)) {
      tmpAlertData.type = "error";
      tmpAlertData.message = "검색 기간이 없습니다. 검색 기간을 설정해주세요.";
      setAlertDialogData(tmpAlertData);
      setIsAlertOpen(true);
      if (isConfirmLoading) setIsConfirmLoading(false);
      return;
    }

    const isConfirmed = business.isAccountingConfirmed;
    const paramData: BaseParamData = {
      startDate: spStartDate!,
      endDate: spEndDate!,
    };
    if (subType) {
      const subTypeKey = SUB_TYPE_PARAM_KEYS[storeType] as SubType;
      if (subTypeKey) paramData[subTypeKey as keyof BaseParamData] = subType;
    }

    setIsConfirmLoading(true);
    updateConfirmation(storeType, business.id, !isConfirmed, paramData) // 마감 완료 버튼 입력시 isAccountingConfirmed = true
      .then(() => {
        setIsConfirmLoading(false);
        tmpAlertData.type = "success";
        tmpAlertData.message =
          (isConfirmed ? "마감 해제" : "마감 설정") + "되었습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        loadStore();
      })
      .catch((err) => {
        setIsConfirmLoading(false);
        tmpAlertData.type = "error";
        tmpAlertData.message =
          (isConfirmed ? "마감 해제" : "마감 설정") +
          " 중 오류가 발생했습니다.";
        setAlertDialogData(tmpAlertData);
        setIsAlertOpen(true);
        console.error(err);
      });
  };

  const checkModification = () => {
    if (modifiedDataMap.size > 0) {
      openUnsavedAlertDialog();
    } else {
      toDashboard();
    }
  };

  const toDashboard = () => {
    // setModifiedDataMap(new Map());
    // setCheckedDataSet(new Set());
    // setBusiness(null);
    const destination =
      spStartDate && spEndDate
        ? `/${menuType}?startDate=${spStartDate}&endDate=${spEndDate}`
        : `/${menuType}`;
    navigate(destination);
  };

  const openUnsavedAlertDialog = () => {
    setIsUnsavedAlertOpen(true);
  };

  const closeUnsavedAlertDialog = () => {
    setIsUnsavedAlertOpen(false);
  };

  const unprocessShowHandler = () => {
    setIsShowUnprocessed(!isShowUnprocessed);
  };

  const filterProcessedData = (dataArray: (CardBuy | BankStatement)[]) => {
    if (isShowUnprocessed) {
      const unproData = dataArray.filter(
        (data) =>
          !data.accountCode ||
          data.accountCode === "000" || // 임시출금
          data.accountCode === "001" // 임시입금
      );

      if (unproData.length > 0) {
        return unproData;
      } else {
        // 보여줄 미처리 데이터가 없는 경우 자동 해제
        setIsShowUnprocessed(false);
        return dataArray;
      }
    } else return dataArray;
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 24px;
      `}
    >
      <section
        css={css`
          padding-top: 24px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        `}
      >
        <div>
          <BaseButton
            onClick={checkModification}
            css={css`
              color: ${PALETTE.textSub};
              padding: 4px;

              &:hover {
                color: ${PALETTE.text};
              }
            `}
          >
            {"< 전체 업체 목록"}
          </BaseButton>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <h1>회계 검수</h1>
          {business && (
            <SubtitleCard>
              <div>{business.name}</div>
              <div>{business.regNumber}</div>
              {business.cluster && (
                <div>
                  {business.cluster}{" "}
                  {business.subCluster && <span>/ {business.subCluster}</span>}
                </div>
              )}
            </SubtitleCard>
          )}
        </div>
      </section>

      <section>
        <div
          css={css`
            margin-bottom: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 16px;
            `}
          >
            <BaseSelect
              name="반기/분기"
              options={reverseArray(HALF_AND_QUARTERS)}
              selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
              onSelect={onSelectQuarter}
            />
            <BaseSelect
              name="월별검색"
              options={reverseArray(MONTHS)}
              selectedOption={convertDateToPeriod(spStartDate, spEndDate)}
              onSelect={onSelectMonth}
            />
            <BaseInput
              placeholder="검색어 입력"
              ref={inputKeywordRef}
              onInput={inputKeywordHandler}
              css={css`
                box-sizing: border-box;
              `}
            />
          </div>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <Tooltip title="계정과목 입력이 완료된 데이터만 엑셀로 다운로드가 가능합니다.">
              <div>
                <BaseButton
                  variant="outlined"
                  color={PALETTE.primary}
                  disabled={dataList.length === 0 || isDownloadLoading}
                  onClick={() => downloadExcel(storeType)}
                >
                  다운로드
                </BaseButton>
              </div>
            </Tooltip>
            <Tooltip
              title={
                business?.isAccountingConfirmed
                  ? "마감된 데이터는 저장할 수 없습니다."
                  : modifiedDataMap.size === 0
                  ? "변경된 데이터가 없습니다."
                  : ""
              }
            >
              <Grid
                position="relative"
                onMouseEnter={() =>
                  storeType === "bank" && setIsSaveOptionOpen(true)
                }
                onMouseLeave={() => setIsSaveOptionOpen(false)}
              >
                <BaseButton
                  variant="contained"
                  disabled={
                    isLoading ||
                    isConfirmLoading ||
                    modifiedDataMap.size === 0 ||
                    business?.isAccountingConfirmed
                  }
                  color={PALETTE.primary}
                  onClick={() => saveModified()}
                >
                  저장
                  {storeType === "bank" && (
                    <>
                      {isRegisterAccountCode ? (
                        <CheckBoxIcon
                          sx={{
                            marginLeft: "2px",
                            marginRight: "-6px",
                            fontSize: "12px",
                          }}
                        />
                      ) : (
                        <CheckBoxOutlineBlankIcon
                          sx={{
                            marginLeft: "2px",
                            marginRight: "-6px",
                            fontSize: "12px",
                          }}
                        />
                      )}
                    </>
                  )}
                </BaseButton>
                {!(
                  isLoading ||
                  isConfirmLoading ||
                  modifiedDataMap.size === 0 ||
                  business?.isAccountingConfirmed
                ) &&
                  isSaveOptionOpen && (
                    <Box
                      minWidth="156px"
                      position="absolute"
                      top="-38px"
                      left="0px"
                      paddingRight="4px"
                      borderRadius="4px 4px 4px 0"
                      boxShadow="0 2px 8px rgba(0, 0, 0, 0.15)"
                      bgcolor={PALETTE.light}
                    >
                      <FormControlLabel
                        label={
                          <Typography fontSize="13px" fontWeight={500}>
                            거래처-계정과목 매칭
                          </Typography>
                        }
                        sx={{ margin: 0 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={isRegisterAccountCode}
                            onChange={() =>
                              setIsRegisterAccountCode(!isRegisterAccountCode)
                            }
                            css={css`
                              svg {
                                fill: ${PALETTE.primary};
                              }
                            `}
                          />
                        }
                      />
                    </Box>
                  )}
              </Grid>
            </Tooltip>
            <BaseButton
              variant={
                business && business.isAccountingConfirmed
                  ? "outlined"
                  : "contained"
              }
              color={PALETTE.primary}
              disabled={
                isLoading ||
                isConfirmLoading ||
                !(
                  business &&
                  business.totalDataNumber &&
                  business.completedDataNumber === business.totalDataNumber
                )
              }
              onClick={() => handleConfirm()}
            >
              {business?.isAccountingConfirmed ? "마감 해제" : "마감 설정"}
            </BaseButton>
          </div>
        </div>

        <div
          css={css`
            margin-bottom: 12px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
            font-size: 16px;
            font-weight: 500;
          `}
        >
          {business ? (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 24px;
              `}
            >
              <div>
                검색기간 {spStartDate?.replaceAll("-", ".")} ~{" "}
                {spEndDate?.replaceAll("-", ".")}
              </div>
              <div>총 {business.totalDataNumber}건</div>
              <UnprocessStatus
                isShow={isShowUnprocessed}
                showHandler={unprocessShowHandler}
                storeType={storeType}
                dataNum={
                  business.totalDataNumber - business.completedDataNumber
                }
              />
              <ReportButton
                storeType={storeType}
                subType={subType}
                reportType="sync"
                bizId={locationBizId}
                selectedDataIds={Array.from(checkedDataSet)}
                disabled={
                  checkedDataSet.size === 0 ||
                  isLoading ||
                  isConfirmLoading ||
                  modifiedDataMap.size > 0
                }
                successCallback={loadStore}
              />
            </div>
          ) : (
            <div></div>
          )}

          <div
            css={css`
              position: relative;
              display: flex;
              align-items: center;
              gap: 12px;
            `}
          >
            <Grid fontSize={14} color={PALETTE.primary}>
              {business?.isAccountingConfirmed
                ? "마감된 데이터는 수정이 불가능합니다."
                : ""}
            </Grid>
            {(menuType === "card" || menuType === "tax-buy") && (
              <BaseSelect
                name="공제여부"
                color={PALETTE.primary}
                disabled={isCheckOverLimit}
                minWidth={120}
                options={[
                  { value: "y", label: "공제" },
                  { value: "n", label: "불공제" },
                ]}
                selectedOption={selectedDdcAll}
                onSelect={onSelectDdcAll}
                css={css`
                  padding: 6px 12px;
                `}
              />
            )}
            <Box
              css={css`
                position: relative;
              `}
            >
              <BaseInput
                placeholder="계정코드 입력"
                color={PALETTE.primary}
                ref={inputSubjectRef}
                disabled={isCheckOverLimit}
                onInput={onSearchSbjAll}
                onKeyDown={keyDownHanlder}
                onFocus={() => {
                  setIsSubjectListOpen(true);
                }}
                onBlur={(e) => {
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (
                    !relatedTarget ||
                    relatedTarget?.tagName !== "LI" ||
                    relatedTarget?.parentNode !== optionSubjectRef.current
                  ) {
                    setIsSubjectListOpen(false);
                  }
                }}
                css={css`
                  box-sizing: border-box;
                  padding: 8px 12px;
                `}
              />
              {isSubjectListOpen && (
                <ul
                  css={css`
                    margin: 0;
                    padding: 0;
                    position: absolute;
                    top: 100%;
                    left: 0;
                    width: 100%;
                    max-height: 200px;
                    overflow-y: auto;
                    border-radius: 4px;
                    box-sizing: border-box;
                    border: 1px solid ${PALETTE.border};
                    background-color: white;
                    z-index: 20;
                    list-style: none;
                  `}
                  ref={optionSubjectRef}
                >
                  {filteredSubjectList.map((subject) => (
                    <li
                      key={subject.value}
                      tabIndex={-1}
                      onClick={() => {
                        onSelectSbjAll({
                          currentTarget: { value: subject.value },
                        } as unknown as React.MouseEvent<HTMLOptionElement>);
                        setIsSubjectListOpen(false);
                      }}
                      css={css`
                        padding: 8px 12px;
                        cursor: pointer;
                        list-style: none;
                        font-size: 14px;

                        &:hover {
                          background-color: ${PALETTE.primaryHover};
                        }
                      `}
                    >
                      {subject.label}
                    </li>
                  ))}
                </ul>
              )}
            </Box>

            {menuType === "bank" && (
              <Grid>
                <BaseButton
                  variant="outlined"
                  color={PALETTE.primary}
                  disabled={isCheckOverLimit}
                  onClick={onSelectStoreAll}
                  css={css`
                    padding: 8px 12px;
                    font-size: 14px;
                  `}
                >
                  거래처 변경
                </BaseButton>
              </Grid>
            )}

            <Tooltip
              title={
                isLoading || isConfirmLoading
                  ? "로딩중입니다."
                  : modifiedDataMap.size > 0
                  ? "수정 중인 데이터가 있습니다. 저장 후 전송 작업을 진행해주세요."
                  : business
                  ? ""
                  : "업체 정보를 불러오지 못했습니다."
              }
            >
              <div>
                <ReportButton
                  storeType={storeType}
                  subType={subType}
                  reportType="submit"
                  bizId={locationBizId}
                  selectedDataIds={Array.from(checkedDataSet)}
                  disabled={
                    checkedDataSet.size === 0 ||
                    isLoading ||
                    isConfirmLoading ||
                    modifiedDataMap.size > 0
                  }
                  variant="contained"
                  successCallback={loadStore}
                  css={css`
                    padding: 8px 12px;
                    font-size: 14px;
                  `}
                >
                  전자신고 전송
                </ReportButton>
              </div>
            </Tooltip>

            <div
              css={css`
                display: ${checkedDataSet.size > checkLimitNumber
                  ? "flex"
                  : "none"};
                width: calc(100% - 114px);
                height: 100%;
                margin-right: 114px;
                border-radius: 4px;
                box-sizing: border-box;
                border: 1px solid ${PALETTE.error};
                background-color: rgba(255, 255, 255, 0.7);
                position: absolute;
                top: 0;
                right: 0;
                align-items: center;
                justify-content: center;
              `}
            >
              {checkedDataSet.size > checkLimitNumber && (
                <span
                  css={css`
                    color: ${PALETTE.error};
                    font-size: 14px;
                    font-weight: 500;
                  `}
                >
                  최대 {checkLimitNumber}건까지 일괄 변경 가능합니다.
                </span>
              )}
            </div>
          </div>
        </div>

        <Table
          isLoading={isLoading}
          minWidth={1240}
          sortLimit={2}
          selectable={true}
          disableStatus={{
            input: business?.isAccountingConfirmed,
            doubleClick: business?.isAccountingConfirmed,
            openDialog: modifiedDataMap.size > 0,
            cancel: modifiedDataMap.size > 0,
          }}
          checkedSet={checkedDataSet}
          setCheckedSet={setCheckedDataSet}
          modifiedMap={modifiedDataMap}
          columns={columnList}
          dataList={filterProcessedData(
            isFiltered ? filteredDataList : sortedDataList
          )}
          subjectList={subjectCodeDataList}
        />
      </section>

      <Dialog open={isUnsavedAlertOpen} onClose={closeUnsavedAlertDialog}>
        <DialogTitle>알림</DialogTitle>
        <DialogContent>
          <DialogContentText>
            저장되지 않은 데이터가 있습니다.
          </DialogContentText>
          <DialogContentText>
            변경사항을 저장하지 않고 이동하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions
          css={css`
            margin: 0 12px;
            padding: 12px;
            border-top: 1px solid ${PALETTE.border};
          `}
        >
          <BaseButton
            variant="outlined"
            color={PALETTE.primary}
            onClick={closeUnsavedAlertDialog}
            css={css`
              padding: 8px 16px;
            `}
          >
            취소
          </BaseButton>
          <BaseButton
            variant="contained"
            color={PALETTE.primary}
            onClick={toDashboard}
            css={css`
              padding: 8px 16px;
            `}
          >
            확인
          </BaseButton>
        </DialogActions>
      </Dialog>
      <AlertDialog
        isOpen={isAlertOpen}
        setIsOpen={setIsAlertOpen}
        alertData={alertDialogData}
      />
      {selectedMAData && (
        <MultiAccountDialog
          isOpen={isMultiAccountDialogOpen}
          onClose={closeMultiAccountCodeDialog}
          data={selectedMAData}
          subjectList={modifiedSubjectList}
          successCallback={loadStore}
        />
      )}
      <SelectStoreDialog
        isOpen={isSelectDialogOpen}
        onClose={closeSelectStoreDialog}
        settings={selectedStoreDialogSettings}
        storeList={storeList}
        businessId={locationBizId}
        successCallback={loadStore}
      />
    </div>
  );
}

const UnprocessStatus = ({
  isShow,
  showHandler,
  storeType = "card",
  dataNum,
}: {
  isShow: boolean;
  showHandler: () => void;
  storeType?: StoreType;
  dataNum: number;
}) => {
  return (
    <div>
      미처리{" "}
      {dataNum > 0 || isShow ? (
        storeType === "bank" ? ( // 은행만 기능 활성화 (카드는 프로세스 0 or 100이므로 불필요)
          <BaseButton
            variant={isShow ? "contained" : "outlined"}
            color={PALETTE.error}
            style={{
              padding: "2px 6px 1px",
              fontSize: "13px",
              transform: "translateY(-1px)",
            }}
            onClick={showHandler}
          >
            {dataNum}건
          </BaseButton>
        ) : (
          <span
            css={css`
              color: red;
            `}
          >
            {dataNum}건
          </span>
        )
      ) : (
        <span>{dataNum}건</span>
      )}
    </div>
  );
};

export default Review;
